<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { toast } from "@/common/utils/toast";
// import { subscriptionShow } from "@/common/api/rest/";
// import { invoiceShow } from "@/common/api/rest/payment/invoice";

import { DefaultTheme } from "@/common/themes";
// import type { SubscriptionType } from "@/common/types/subscription/subscription";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const isLoading = ref(true);
const subscription = ref<any | null>(null);
const invoice = ref<any>(null);

const paymentStatus = computed(() => {
  if (!subscription.value) return "processing";

  switch (subscription.value.status) {
    case "PAID":
      return "success";
    case "PENDING":
      return "pending";
    case "CANCELED":
      return "canceled";
    case "REFUNDED":
      return "refunded";
    default:
      return "processing";
  }
});

const statusIcon = computed(() => {
  switch (paymentStatus.value) {
    case "success":
      return "✅";
    case "pending":
      return "⏳";
    case "canceled":
      return "❌";
    case "refunded":
      return "↩️";
    default:
      return "🔄";
  }
});

const statusTitle = computed(() => {
  switch (paymentStatus.value) {
    case "success":
      return t("checkout.success.title");
    case "pending":
      return t("checkout.success.pending_title");
    case "canceled":
      return t("checkout.success.canceled_title");
    case "refunded":
      return t("checkout.success.refunded_title");
    default:
      return t("checkout.success.processing_title");
  }
});

const statusMessage = computed(() => {
  switch (paymentStatus.value) {
    case "success":
      return t("checkout.success.message");
    case "pending":
      return t("checkout.success.pending_message");
    case "canceled":
      return t("checkout.success.canceled_message");
    case "refunded":
      return t("checkout.success.refunded_message");
    default:
      return t("checkout.success.processing_message");
  }
});

async function fetchSubscriptionData() {
  //   try {
  //     // Buscar subscription_id da URL ou do store
  //     const subscriptionId = route.query.subscription_id as string;
  //     if (!subscriptionId) {
  //       // Se não tem subscription_id, tentar buscar do store
  //       const userSubscription = store.getters.user?.subscription_id;
  //       if (!userSubscription) {
  //         // Redirecionar para planos se não encontrar
  //         router.push("/plans");
  //         return;
  //       }
  //     }
  //     // Buscar dados da subscription
  //     if (subscriptionId) {
  //       const response = await subscriptionShow(subscriptionId);
  //       if (response.success) {
  //         subscription.value = response.data;
  //         // Se a subscription tem invoice_id, buscar invoice
  //         if (response.data.metadata) {
  //           try {
  //             const metadata = JSON.parse(response.data.metadata);
  //             if (metadata.invoice_id) {
  //               const invoiceResponse = await invoiceShow(metadata.invoice_id);
  //               if (invoiceResponse.success) {
  //                 invoice.value = invoiceResponse.data;
  //               }
  //             }
  //           } catch (error) {
  //             console.error("Error parsing metadata:", error);
  //           }
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error fetching subscription:", error);
  //     toast({
  //       message: t("error.default"),
  //       type: "error",
  //     });
  //   } finally {
      isLoading.value = false;
  //   }
}

function goToDashboard() {
  router.push("/dashboard");
}

function goToPlans() {
  router.push("/plans");
}

function goToSupport() {
  router.push("/support");
}

onMounted(() => {
  fetchSubscriptionData();
});
</script>

<template>
  <default-theme>
    <section class="success-page">
      <div v-if="isLoading" class="loading">
        <div class="spinner"></div>
        <p>{{ t("common.loading") }}</p>
      </div>

      <div v-else class="success-container">
        <div class="status-card" :class="paymentStatus">
          <div class="status-icon">{{ statusIcon }}</div>

          <h1 class="status-title">{{ statusTitle }}</h1>

          <p class="status-message">{{ statusMessage }}</p>

          <!-- Detalhes da subscription -->
          <div v-if="subscription" class="subscription-details">
            <div class="detail-row">
              <span class="detail-label">{{
                t("checkout.success.subscription_id")
              }}</span>
              <span class="detail-value">{{
                subscription.subscription_id
              }}</span>
            </div>

            <div v-if="subscription.stripe_subscription_id" class="detail-row">
              <span class="detail-label">{{
                t("checkout.success.stripe_id")
              }}</span>
              <span class="detail-value">{{
                subscription.stripe_subscription_id
              }}</span>
            </div>

            <div class="detail-row">
              <span class="detail-label">{{
                t("checkout.success.status")
              }}</span>
              <span class="detail-value status-badge" :class="paymentStatus">
                {{ t(`checkout.success.status_${paymentStatus}`) }}
              </span>
            </div>

            <!-- Detalhes da invoice se disponível -->
            <template v-if="invoice">
              <div class="detail-row" v-if="invoice.amount">
                <span class="detail-label">{{
                  t("checkout.success.amount")
                }}</span>
                <span class="detail-value">
                  {{
                    new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: invoice.currency || "BRL",
                    }).format(invoice.amount / 100)
                  }}
                </span>
              </div>

              <div class="detail-row" v-if="invoice.paid_at">
                <span class="detail-label">{{
                  t("checkout.success.paid_at")
                }}</span>
                <span class="detail-value">{{
                  new Date(invoice.paid_at).toLocaleDateString("pt-BR")
                }}</span>
              </div>
            </template>
          </div>

          <!-- Ações -->
          <div class="action-buttons">
            <button
              v-if="paymentStatus === 'success'"
              class="btn btn-primary"
              @click="goToDashboard"
            >
              {{ t("checkout.success.go_to_dashboard") }}
            </button>

            <button
              v-if="
                paymentStatus === 'canceled' || paymentStatus === 'refunded'
              "
              class="btn btn-primary"
              @click="goToPlans"
            >
              {{ t("checkout.success.go_to_plans") }}
            </button>

            <button
              v-if="paymentStatus === 'pending'"
              class="btn btn-secondary"
              @click="goToSupport"
            >
              {{ t("checkout.success.contact_support") }}
            </button>
          </div>

          <!-- Link de volta -->
          <div class="back-link">
            <a @click.prevent="goToPlans" class="link">
              {{ t("checkout.success.back_to_plans") }}
            </a>
          </div>
        </div>

        <!-- Próximos passos -->
        <div v-if="paymentStatus === 'success'" class="next-steps">
          <h2>{{ t("checkout.success.next_steps_title") }}</h2>

          <div class="steps-list">
            <div class="step-item">
              <div class="step-number">1</div>
              <div class="step-content">
                <h3>{{ t("checkout.success.step1_title") }}</h3>
                <p>{{ t("checkout.success.step1_description") }}</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">2</div>
              <div class="step-content">
                <h3>{{ t("checkout.success.step2_title") }}</h3>
                <p>{{ t("checkout.success.step2_description") }}</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">3</div>
              <div class="step-content">
                <h3>{{ t("checkout.success.step3_title") }}</h3>
                <p>{{ t("checkout.success.step3_description") }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </default-theme>
</template>

<style lang="scss" scoped>
.success-page {
  min-height: 100vh;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 300px;

  .spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e2e8f0;
    border-top: 4px solid #1e293b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    color: #475569;
    font-size: 1.1rem;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.success-container {
  max-width: 600px;
  width: 100%;
}

.status-card {
  background: #fff;
  border-radius: 20px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &.success {
    border-top: 4px solid #10b981;
  }

  &.pending {
    border-top: 4px solid #f59e0b;
  }

  &.canceled {
    border-top: 4px solid #ef4444;
  }

  &.refunded {
    border-top: 4px solid #8b5cf6;
  }

  &.processing {
    border-top: 4px solid #3b82f6;
  }
}

.status-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  animation: bounce-in 0.5s ease;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.status-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.status-message {
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.subscription-details {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.detail-value {
  color: #1e293b;
  font-weight: 500;
  font-size: 0.9rem;
  max-width: 60%;
  text-align: right;
  word-break: break-all;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;

  &.success {
    background: #d1fae5;
    color: #065f46;
  }

  &.pending {
    background: #fed7aa;
    color: #92400e;
  }

  &.canceled {
    background: #fee2e2;
    color: #991b1b;
  }

  &.refunded {
    background: #ede9fe;
    color: #5b21b6;
  }

  &.processing {
    background: #dbeafe;
    color: #1e40af;
  }
}

.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1.5rem;

  .btn {
    padding: 0.875rem 1.5rem;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;

    &-primary {
      background: #1e293b;
      color: #fff;

      &:hover {
        background: #0f172a;
        transform: translateY(-1px);
      }
    }

    &-secondary {
      background: #f59e0b;
      color: #fff;

      &:hover {
        background: #d97706;
        transform: translateY(-1px);
      }
    }
  }
}

.back-link {
  .link {
    color: #64748b;
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s;

    &:hover {
      color: #1e293b;
      text-decoration: underline;
    }
  }
}

.next-steps {
  margin-top: 2rem;
  background: #fff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1.5rem;
    text-align: center;
  }
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.step-number {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: #1e293b;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.step-content {
  flex: 1;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 0.25rem;
  }

  p {
    font-size: 0.9rem;
    color: #64748b;
    line-height: 1.5;
  }
}

@media (max-width: 480px) {
  .status-card {
    padding: 2rem 1.5rem;
  }

  .status-title {
    font-size: 1.5rem;
  }

  .action-buttons {
    flex-direction: column;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .detail-value {
    max-width: 100%;
    text-align: left;
  }
}
</style>
